/**
 * Date Range Picker Component with Preset Options
 *
 * Provides quick selection of common date ranges:
 * - This Month
 * - Last Month
 * - Last Quarter
 * - Year to Date
 * - Custom Range
 */

import { renderIcon } from '../utils/icons';

/**
 * Date range preset options
 */
export type DateRangePreset = 'this-month' | 'last-month' | 'last-quarter' | 'ytd' | 'all-time' | 'custom';

/**
 * Date range value
 */
export interface DateRange {
  fromDate: string;
  toDate: string;
  preset: DateRangePreset;
}

/**
 * Date range picker configuration
 */
export interface DateRangePickerConfig {
  id: string;
  name?: string;
  value?: DateRange;
  onChange?: (value: DateRange) => void;
  className?: string;
}

/**
 * Calculate date range based on preset
 */
function calculateDateRange(preset: DateRangePreset): { fromDate: string; toDate: string } {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();

  let fromDate: Date;
  let toDate: Date = new Date();

  switch (preset) {
    case 'this-month':
      fromDate = new Date(year, month, 1);
      toDate = new Date(year, month + 1, 0);
      break;

    case 'last-month':
      fromDate = new Date(year, month - 1, 1);
      toDate = new Date(year, month, 0);
      break;

    case 'last-quarter':
      const currentQuarter = Math.floor(month / 3);
      const lastQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
      const lastQuarterYear = currentQuarter === 0 ? year - 1 : year;
      fromDate = new Date(lastQuarterYear, lastQuarter * 3, 1);
      toDate = new Date(lastQuarterYear, (lastQuarter + 1) * 3, 0);
      break;

    case 'ytd':
      fromDate = new Date(year, 0, 1);
      toDate = new Date();
      break;

    case 'all-time':
      // Set a very early date to capture all historical data
      fromDate = new Date(2000, 0, 1);
      toDate = new Date();
      break;

    default: // custom
      // Default to last 30 days for custom
      fromDate = new Date();
      fromDate.setDate(fromDate.getDate() - 30);
      toDate = new Date();
  }

  // Format as YYYY-MM-DD
  const formatDate = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  return {
    fromDate: formatDate(fromDate),
    toDate: formatDate(toDate),
  };
}

/**
 * Create a date range picker component
 */
export function createDateRangePicker(config: DateRangePickerConfig): HTMLElement {
  const container = document.createElement('div');
  container.className = `relative ${config.className || ''}`;

  // Current state
  let currentRange: DateRange = config.value || {
    ...calculateDateRange('this-month'),
    preset: 'this-month',
  };

  // Preset buttons
  const presetButtons = document.createElement('div');
  presetButtons.className = 'flex flex-wrap gap-2 mb-3';

  const presets: { value: DateRangePreset; label: string; icon: string }[] = [
    { value: 'this-month', label: 'This Month', icon: 'calendar' },
    { value: 'last-month', label: 'Last Month', icon: 'calendar' },
    { value: 'last-quarter', label: 'Last Quarter', icon: 'pieChart' },
    { value: 'ytd', label: 'YTD', icon: 'trendingUp' },
    { value: 'all-time', label: 'All Time', icon: 'infinity' },
    { value: 'custom', label: 'Custom', icon: 'settings' },
  ];

  const presetButtonElements: Map<DateRangePreset, HTMLButtonElement> = new Map();

  presets.forEach(preset => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `
      px-3 py-1.5 rounded-md text-sm font-medium transition-all
      ${currentRange.preset === preset.value
        ? 'bg-primary text-primary-foreground'
        : 'glass-button text-foreground hover:bg-muted'}
    `;
    button.innerHTML = `
      ${renderIcon(preset.icon as any, { size: 'xs' }).outerHTML}
      <span>${preset.label}</span>
    `;
    button.style.display = 'inline-flex';
    button.style.alignItems = 'center';
    button.style.gap = '0.375rem';

    button.addEventListener('click', () => {
      selectPreset(preset.value);
    });

    presetButtonElements.set(preset.value, button);
    presetButtons.appendChild(button);
  });

  container.appendChild(presetButtons);

  // Date inputs container
  const dateInputsContainer = document.createElement('div');
  dateInputsContainer.className = 'flex items-center gap-3';

  // From date input
  const fromDateWrapper = document.createElement('div');
  fromDateWrapper.className = 'flex-1';

  const fromDateLabel = document.createElement('label');
  fromDateLabel.className = 'block text-xs font-medium text-muted-foreground mb-1';
  fromDateLabel.textContent = 'From';
  fromDateWrapper.appendChild(fromDateLabel);

  const fromDateInput = document.createElement('input');
  fromDateInput.type = 'date';
  fromDateInput.className = 'w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring';
  fromDateInput.value = currentRange.fromDate;
  fromDateInput.id = `${config.id}-from`;
  if (config.name) fromDateInput.name = `${config.name}-from`;

  fromDateInput.addEventListener('change', () => {
    handleCustomDateChange();
  });

  fromDateWrapper.appendChild(fromDateInput);
  dateInputsContainer.appendChild(fromDateWrapper);

  // Separator
  const separator = document.createElement('div');
  separator.className = 'pt-5';
  separator.innerHTML = renderIcon('arrowRight', { size: 'sm', className: 'text-muted-foreground' }).outerHTML;
  dateInputsContainer.appendChild(separator);

  // To date input
  const toDateWrapper = document.createElement('div');
  toDateWrapper.className = 'flex-1';

  const toDateLabel = document.createElement('label');
  toDateLabel.className = 'block text-xs font-medium text-muted-foreground mb-1';
  toDateLabel.textContent = 'To';
  toDateWrapper.appendChild(toDateLabel);

  const toDateInput = document.createElement('input');
  toDateInput.type = 'date';
  toDateInput.className = 'w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring';
  toDateInput.value = currentRange.toDate;
  toDateInput.id = `${config.id}-to`;
  if (config.name) toDateInput.name = `${config.name}-to`;

  toDateInput.addEventListener('change', () => {
    handleCustomDateChange();
  });

  toDateWrapper.appendChild(toDateInput);
  dateInputsContainer.appendChild(toDateWrapper);

  container.appendChild(dateInputsContainer);

  /**
   * Select a preset date range
   */
  function selectPreset(preset: DateRangePreset) {
    // Update button styles
    presetButtonElements.forEach((button, key) => {
      if (key === preset) {
        button.className = 'px-3 py-1.5 rounded-md text-sm font-medium transition-all bg-primary text-primary-foreground';
      } else {
        button.className = 'px-3 py-1.5 rounded-md text-sm font-medium transition-all glass-button text-foreground hover:bg-muted';
      }
    });

    if (preset !== 'custom') {
      const range = calculateDateRange(preset);
      currentRange = { ...range, preset };
      fromDateInput.value = range.fromDate;
      toDateInput.value = range.toDate;
    } else {
      currentRange.preset = 'custom';
    }

    if (config.onChange) {
      config.onChange(currentRange);
    }
  }

  /**
   * Handle custom date input changes
   */
  function handleCustomDateChange() {
    currentRange = {
      fromDate: fromDateInput.value,
      toDate: toDateInput.value,
      preset: 'custom',
    };

    selectPreset('custom');
  }

  /**
   * Get current date range
   */
  function getValue(): DateRange {
    return currentRange;
  }

  /**
   * Set date range programmatically
   */
  function setValue(value: DateRange) {
    currentRange = value;
    fromDateInput.value = value.fromDate;
    toDateInput.value = value.toDate;
    selectPreset(value.preset);
  }

  // Attach methods to container
  (container as any).getValue = getValue;
  (container as any).setValue = setValue;

  return container;
}